import { i18n } from '@lingui/core';
import {
  BasicDisplayUnits, unitLabels, additionalUnitLabels, UnitBase, UnitOfMeasurement, localiseUnitBase
} from 'hooks/useDisplayUnits/useDisplayUnits';
import { DisplayUnits, ThroughputControlType, UnitSystem } from 'providers/api';

export const ThroughputControlTypeLabel: Record<ThroughputControlType, string> = {
  [ThroughputControlType.DryTonnage]: i18n._('Dry Tonnage'),
  [ThroughputControlType.WetTonnage]: i18n._('Wet Tonange'),
  [ThroughputControlType.FlowRate]: i18n._('Flow Rate'),
};

export const ThroughputControlTypeAbbrev: Record<UnitSystem, Record<ThroughputControlType, string>> = {
  [UnitSystem.Metric]: {
    [ThroughputControlType.DryTonnage]: unitLabels[BasicDisplayUnits.DryTonnage].metric.abbreviation,
    [ThroughputControlType.WetTonnage]: unitLabels[BasicDisplayUnits.WetTonnage].metric.abbreviation,
    [ThroughputControlType.FlowRate]: additionalUnitLabels[DisplayUnits.FlowRate].metric.abbreviation,
  },
  [UnitSystem.Imperial]: {
    [ThroughputControlType.DryTonnage]: unitLabels[BasicDisplayUnits.DryTonnage].imperial.abbreviation,
    [ThroughputControlType.WetTonnage]: unitLabels[BasicDisplayUnits.WetTonnage].imperial.abbreviation,
    [ThroughputControlType.FlowRate]: additionalUnitLabels[DisplayUnits.FlowRate].imperial.abbreviation,
  },
};

export const ThroughputControlTypeUnitLabels: Record<ThroughputControlType, UnitBase> = {
  [ThroughputControlType.DryTonnage]: unitLabels[BasicDisplayUnits.DryTonnage],
  [ThroughputControlType.WetTonnage]: unitLabels[BasicDisplayUnits.WetTonnage],
  [ThroughputControlType.FlowRate]: additionalUnitLabels[DisplayUnits.FlowRate],
};

export const DisplayUnitAbbrev = (unitSystem: UnitSystem): Record<BasicDisplayUnits, string> => Object.keys(unitLabels).reduce((acc, unitKey) => {
  // Property names of UnitBase — never translate these, they are object keys.
  const keyName: keyof UnitBase = unitSystem === UnitSystem.Imperial ? 'imperial' : 'metric';
  const unit = unitKey as unknown as BasicDisplayUnits;

  const unitLabel = unitLabels[unit];
  const unitOfMeasurement = unitLabel[keyName] as UnitOfMeasurement;

  acc[unit] = unitOfMeasurement.abbreviation;
  return acc;
}, {} as Record<BasicDisplayUnits, string>);

export const DisplayUnitConversion = (unitSystem: UnitSystem): Record<BasicDisplayUnits,
  (currentValue: number) => number> => Object.keys(unitLabels).reduce((acc, unitKey) => {
    // Property names of UnitBase — never translate these, they are object keys.
    const keyName: keyof UnitBase = unitSystem === UnitSystem.Imperial ? 'imperial' : 'metric';
    const unit = unitKey as unknown as BasicDisplayUnits;

    const unitLabel = unitLabels[unit];
    const unitOfMeasurement = unitLabel[keyName] as UnitOfMeasurement;

    acc[unit] = unitOfMeasurement.conversion;
    return acc;
  }, {} as Record<BasicDisplayUnits, (currentValue: number) => number>);

export const DisplayCustomUnitConversion = (unitSystem: UnitSystem):
  Record<BasicDisplayUnits, (currentValue: number) => number> => {
  const convertToMeterByM = (currentValue: number) => parseFloat((currentValue * 1000).toFixed(2));
  const convertToInchByM = (currentValue: number) => parseFloat(((currentValue * 1000) / 25.4).toFixed(2));
  const convertToKpaByKpa = (currentValue: number) => Math.round(currentValue);
  const convertToPSIByKpa = (currentValue: number) => Math.round(currentValue / 6.895);

  const customUnitLabels: Partial<Record<BasicDisplayUnits, UnitBase>> = {
    [BasicDisplayUnits.Diameter]: {
      name: i18n._('Diameter'),
      decimalPlaces: 1,
      metric: {
        abbreviation: 'mm',
        conversion: convertToMeterByM,
      },
      imperial: {
        abbreviation: 'in',
        conversion: convertToInchByM,
      },
    },
    [BasicDisplayUnits.PressureUCS]: {
      name: i18n._('Strength'),
      decimalPlaces: 0,
      metric: {
        abbreviation: 'kPa',
        conversion: convertToKpaByKpa,
      },
      imperial: {
        abbreviation: 'psi',
        conversion: convertToPSIByKpa,
      },
    },
  };

  Object.values(customUnitLabels).forEach(localiseUnitBase);

  return Object.keys(customUnitLabels).reduce((acc, unitKey) => {
    // Property names of UnitBase — never translate these, they are object keys.
    const keyName: keyof UnitBase = unitSystem === UnitSystem.Imperial ? 'imperial' : 'metric';
    const unit = unitKey as unknown as BasicDisplayUnits;

    const unitLabel = customUnitLabels[unit]!;
    const unitOfMeasurement = unitLabel[keyName] as UnitOfMeasurement;

    acc[unit] = unitOfMeasurement.conversion;
    return acc;
  }, {} as Record<BasicDisplayUnits, (currentValue: number) => number>);
};
